import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Calendar, 
  MessageSquare, 
  AlertCircle,
  LogOut,
  CheckCircle2,
  XCircle,
  Clock,
  DollarSign,
  Loader2,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  FileDown
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { t } = useLanguage();
  const { token, logout, admin, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [availabilityRequests, setAvailabilityRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, bookingsRes, requestsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/availability-requests`, { headers }),
        axios.get(`${API}/contact-messages`, { headers }),
      ]);

      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setAvailabilityRequests(requestsRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to update booking status:', error);
    }
  };

  const updateAvailabilityStatus = async (requestId, status) => {
    try {
      await axios.patch(`${API}/availability-requests/${requestId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to update availability status:', error);
    }
  };

  const markMessageRead = async (messageId) => {
    try {
      await axios.patch(`${API}/contact-messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to mark message read:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-500 bg-yellow-500/10';
      case 'confirmed': return 'text-green-500 bg-green-500/10';
      case 'completed': return 'text-blue-500 bg-blue-500/10';
      case 'cancelled': return 'text-red-500 bg-red-500/10';
      case 'responded': return 'text-green-500 bg-green-500/10';
      case 'declined': return 'text-red-500 bg-red-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-[#121212] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 
                className="text-2xl text-white"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {t('admin_dashboard')}
              </h1>
              <p className="text-sm text-[#A1A1AA]">{admin?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-[#A1A1AA] hover:text-white transition-colors"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" />
              {t('admin_logout')}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#121212] border border-white/10 p-1 mb-8">
            <TabsTrigger 
              value="dashboard" 
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
              data-testid="tab-dashboard"
            >
              <LayoutDashboard className="w-4 h-4 mr-2" />
              {t('admin_dashboard')}
            </TabsTrigger>
            <TabsTrigger 
              value="bookings"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
              data-testid="tab-bookings"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {t('admin_bookings')}
            </TabsTrigger>
            <TabsTrigger 
              value="requests"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
              data-testid="tab-requests"
            >
              <AlertCircle className="w-4 h-4 mr-2" />
              {t('admin_requests')}
            </TabsTrigger>
            <TabsTrigger 
              value="messages"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
              data-testid="tab-messages"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              {t('admin_messages')}
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {stats && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-[#121212] border border-white/10 p-6" data-testid="stat-total-bookings">
                  <div className="flex items-center justify-between mb-4">
                    <Calendar className="w-8 h-8 text-[#D4AF37]" />
                    <span className="text-3xl text-white font-light">{stats.total_bookings}</span>
                  </div>
                  <p className="text-[#A1A1AA] text-sm">{t('admin_total_bookings')}</p>
                </div>

                <div className="bg-[#121212] border border-white/10 p-6" data-testid="stat-pending">
                  <div className="flex items-center justify-between mb-4">
                    <Clock className="w-8 h-8 text-yellow-500" />
                    <span className="text-3xl text-white font-light">{stats.pending_bookings}</span>
                  </div>
                  <p className="text-[#A1A1AA] text-sm">{t('admin_pending')}</p>
                </div>

                <div className="bg-[#121212] border border-white/10 p-6" data-testid="stat-confirmed">
                  <div className="flex items-center justify-between mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                    <span className="text-3xl text-white font-light">{stats.confirmed_bookings}</span>
                  </div>
                  <p className="text-[#A1A1AA] text-sm">{t('admin_confirmed')}</p>
                </div>

                <div className="bg-[#121212] border border-[#D4AF37]/30 p-6" data-testid="stat-revenue">
                  <div className="flex items-center justify-between mb-4">
                    <DollarSign className="w-8 h-8 text-[#D4AF37]" />
                    <span className="text-3xl gold-text font-light">CHF {stats.total_revenue}</span>
                  </div>
                  <p className="text-[#A1A1AA] text-sm">{t('admin_revenue')}</p>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#121212] border border-white/10 p-6">
                <h3 className="text-lg text-white mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Pending Actions
                </h3>
                <div className="space-y-3">
                  {stats?.pending_bookings > 0 && (
                    <button
                      onClick={() => setActiveTab('bookings')}
                      className="w-full flex items-center justify-between p-3 bg-[#0A0A0A] hover:bg-white/5 transition-colors text-left"
                    >
                      <span className="text-[#A1A1AA]">{stats.pending_bookings} pending bookings</span>
                      <ChevronRight className="w-4 h-4 text-[#D4AF37]" />
                    </button>
                  )}
                  {stats?.availability_requests > 0 && (
                    <button
                      onClick={() => setActiveTab('requests')}
                      className="w-full flex items-center justify-between p-3 bg-[#0A0A0A] hover:bg-white/5 transition-colors text-left"
                    >
                      <span className="text-[#A1A1AA]">{stats.availability_requests} availability requests</span>
                      <ChevronRight className="w-4 h-4 text-[#D4AF37]" />
                    </button>
                  )}
                  {stats?.unread_messages > 0 && (
                    <button
                      onClick={() => setActiveTab('messages')}
                      className="w-full flex items-center justify-between p-3 bg-[#0A0A0A] hover:bg-white/5 transition-colors text-left"
                    >
                      <span className="text-[#A1A1AA]">{stats.unread_messages} unread messages</span>
                      <ChevronRight className="w-4 h-4 text-[#D4AF37]" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <div className="space-y-4">
              {bookings.length === 0 ? (
                <div className="text-center py-12 text-[#A1A1AA]">No bookings yet</div>
              ) : (
                bookings.map((booking) => (
                  <div 
                    key={booking.id} 
                    className="bg-[#121212] border border-white/10 p-6"
                    data-testid={`booking-${booking.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg text-white">{booking.customer_name}</h3>
                          <span className={`px-2 py-1 text-xs uppercase ${getStatusColor(booking.status)}`}>
                            {booking.status}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-[#A1A1AA]">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {booking.pickup_date} {booking.pickup_time}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {booking.customer_email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {booking.customer_phone}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                          <MapPin className="w-4 h-4 text-[#D4AF37]" />
                          {booking.pickup_location} → {booking.dropoff_location}
                        </div>
                        <div className="text-[#D4AF37] font-medium">
                          CHF {booking.calculated_price?.toFixed(2)} • {booking.service_type} • {booking.booking_type}
                        </div>
                      </div>

                      {booking.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                            className="px-4 py-2 bg-green-600 text-white text-sm hover:bg-green-700 transition-colors"
                            data-testid={`confirm-booking-${booking.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                            className="px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700 transition-colors"
                            data-testid={`cancel-booking-${booking.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {booking.status === 'confirmed' && (
                        <button
                          onClick={() => updateBookingStatus(booking.id, 'completed')}
                          className="px-4 py-2 bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors"
                          data-testid={`complete-booking-${booking.id}`}
                        >
                          Complete
                        </button>
                      )}

                      {/* PDF Download */}
                      <a
                        href={`${API}/bookings/${booking.id}/pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-[#D4AF37]/20 text-[#D4AF37] text-sm hover:bg-[#D4AF37]/30 transition-colors border border-[#D4AF37]/30 flex items-center gap-1"
                        data-testid={`pdf-booking-${booking.id}`}
                        title="Download PDF"
                      >
                        <FileDown className="w-4 h-4" />
                        <span className="hidden sm:inline">PDF</span>
                      </a>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Availability Requests Tab */}
          <TabsContent value="requests">
            <div className="space-y-4">
              {availabilityRequests.length === 0 ? (
                <div className="text-center py-12 text-[#A1A1AA]">No availability requests</div>
              ) : (
                availabilityRequests.map((request) => (
                  <div 
                    key={request.id} 
                    className="bg-[#121212] border border-white/10 p-6"
                    data-testid={`request-${request.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg text-white">{request.customer_name}</h3>
                          <span className="px-2 py-1 text-xs uppercase bg-[#D4AF37]/20 text-[#D4AF37]">
                            SPRINTER
                          </span>
                          <span className={`px-2 py-1 text-xs uppercase ${getStatusColor(request.status)}`}>
                            {request.status}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-[#A1A1AA]">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {request.pickup_date} {request.pickup_time}
                          </span>
                          <span>{request.passengers} passengers</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                          <MapPin className="w-4 h-4 text-[#D4AF37]" />
                          {request.pickup_location} → {request.dropoff_location}
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-[#A1A1AA]">
                          <span>{request.customer_email}</span>
                          <span>{request.customer_phone}</span>
                        </div>
                        {request.message && (
                          <p className="text-sm text-[#A1A1AA] italic">"{request.message}"</p>
                        )}
                      </div>

                      {request.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => updateAvailabilityStatus(request.id, 'responded')}
                            className="px-4 py-2 bg-green-600 text-white text-sm hover:bg-green-700 transition-colors"
                            data-testid={`respond-request-${request.id}`}
                          >
                            Responded
                          </button>
                          <button
                            onClick={() => updateAvailabilityStatus(request.id, 'declined')}
                            className="px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700 transition-colors"
                            data-testid={`decline-request-${request.id}`}
                          >
                            Decline
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-12 text-[#A1A1AA]">No messages</div>
              ) : (
                messages.map((message) => (
                  <div 
                    key={message.id} 
                    className={`bg-[#121212] border p-6 ${message.read ? 'border-white/10' : 'border-[#D4AF37]/50'}`}
                    data-testid={`message-${message.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg text-white">{message.name}</h3>
                          {!message.read && (
                            <span className="px-2 py-1 text-xs uppercase bg-[#D4AF37]/20 text-[#D4AF37]">
                              NEW
                            </span>
                          )}
                        </div>
                        <p className="text-[#D4AF37] font-medium">{message.subject}</p>
                        <p className="text-[#A1A1AA] text-sm">{message.message}</p>
                        <div className="flex flex-wrap gap-4 text-sm text-[#A1A1AA]">
                          <span>{message.email}</span>
                          <span>{message.phone}</span>
                        </div>
                      </div>

                      {!message.read && (
                        <button
                          onClick={() => markMessageRead(message.id)}
                          className="px-4 py-2 border border-white/20 text-white text-sm hover:bg-white/5 transition-colors"
                          data-testid={`mark-read-${message.id}`}
                        >
                          Mark Read
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
